"use client";

import { useEffect, useRef, useCallback, useState } from "react";
import { Canvas, FabricObject, Textbox, Rect, Circle, Triangle, Line, FabricImage, ActiveSelection, Group, FabricText } from "fabric";
import DOMPurify from "dompurify";
import type { CanvasData, CanvasElement, TemplatePage, TableCellValue } from "@/types";

// Sentinel written to the system clipboard when copying a canvas object.
// On paste, we only use our internal/localStorage clipboard if this marker is
// still the latest clipboard content. Otherwise the user copied something
// external (text/image) more recently, and that external content should win.
const INTERNAL_CLIPBOARD_MARKER = "__propale_internal_clipboard__";

// Extend fabric object types for custom properties
declare module "fabric" {
  interface FabricObject {
    customType?: string;
    customIsInherited?: boolean; // True for objects inherited from parent template (locked)
    outputSlug?: string;
    inputSlug?: string;
    originalValue?: unknown;
    isEditable?: boolean;
    placeholderType?: "pricing_grid" | "output_value" | "input_value" | "data_table";
    placeholderLabel?: string;
    tableColumns?: Array<{ header: string; output_slug: string }>;
    // Value formatting options (for placeholder_zone)
    prefix?: string;
    suffix?: string;
    decimalPlaces?: number;
    useThousandSeparator?: boolean;
    // Text styling options (for placeholder_zone)
    fontSize?: number;
    fontFamily?: string;
    fontWeight?: string | number;
    textAlign?: string;
    // Table properties
    tableRows?: number;
    tableCols?: number;
    tableData?: TableCellValue[][];
    tableHeaderBgColor?: string;
    tableBorderColor?: string;
    tableHasHeader?: boolean;
    tableHasRowHeader?: boolean;
    tableSkipHideZeroRows?: boolean;
    tableFontSize?: number;
    tableFontFamily?: string;
    tableHeaderTextColor?: string;
    tableFill?: string;
    tableWidth?: number;
    tableHeight?: number;
  }
}

interface ProposalCanvasProps {
  initialData?: CanvasData;
  inheritedData?: CanvasData; // Parent's canvas data - objects will be rendered as locked/non-interactive
  width?: number;
  height?: number;
  readOnly?: boolean;
  onChange?: (canvasData: CanvasData) => void;
  onSelectionChange?: (hasSelection: boolean, selectedObject: FabricObject | null) => void;
  canvasRef?: React.RefObject<HTMLCanvasElement>;
  // External clipboard ref for cross-page copy/paste (persists across page switches)
  clipboardRef?: React.MutableRefObject<FabricObject | null>;
  // Simulation data for rendering placeholder values
  simulationOutputs?: Record<string, unknown>;
  simulationInputs?: Record<string, unknown>;
}

export function ProposalCanvas({
  initialData,
  inheritedData,
  width = 960,
  height = 540,
  readOnly = false,
  onChange,
  onSelectionChange,
  canvasRef: externalCanvasRef,
  clipboardRef: externalClipboardRef,
  simulationOutputs = {},
  simulationInputs = {},
}: ProposalCanvasProps) {
  const internalCanvasRef = useRef<HTMLCanvasElement>(null);
  const canvasRef = externalCanvasRef || internalCanvasRef;
  const fabricRef = useRef<Canvas | null>(null);
  const [isReady, setIsReady] = useState(false);
  const isApplyingDataRef = useRef(false);
  const hasLoadedInitialDataRef = useRef(false);
  // Internal clipboard for copy/paste of canvas objects (preserves dimensions)
  // Use external ref if provided (for cross-page persistence), otherwise use internal
  const internalClipboardRef = useRef<FabricObject | null>(null);
  const copiedObjectRef = externalClipboardRef || internalClipboardRef;

  // Initialize canvas
  useEffect(() => {
    if (!canvasRef.current || fabricRef.current) return;

    const canvas = new Canvas(canvasRef.current, {
      width,
      height,
      backgroundColor: initialData?.background || "#ffffff",
      selection: !readOnly,
    });

    fabricRef.current = canvas;

    // Setup event listeners
    canvas.on("object:modified", (e) => {
      // Normalize textbox dimensions after resize (convert scale to actual width/height)
      const obj = e.target;
      if (obj instanceof Textbox) {
        const scaleX = obj.scaleX || 1;
        const scaleY = obj.scaleY || 1;
        
        // Only normalize if scale has changed from 1
        if (scaleX !== 1 || scaleY !== 1) {
          const newWidth = (obj.width || 200) * scaleX;
          obj.set({
            width: newWidth,
            scaleX: 1,
            scaleY: 1,
          });
          obj.setCoords();
          canvas.renderAll();
        }
      }
      
      if (!isApplyingDataRef.current && onChange) {
        onChange(getCanvasData());
      }
    });

    canvas.on("object:added", () => {
      if (!isApplyingDataRef.current && onChange) {
        onChange(getCanvasData());
      }
    });

    canvas.on("object:removed", () => {
      if (!isApplyingDataRef.current && onChange) {
        onChange(getCanvasData());
      }
    });

    canvas.on("selection:created", () => {
      if (!isApplyingDataRef.current && onSelectionChange) {
        // Pass the active object (could be single object or ActiveSelection)
        const activeObject = canvas.getActiveObject();
        onSelectionChange(true, activeObject || null);
      }
    });

    canvas.on("selection:updated", () => {
      if (!isApplyingDataRef.current && onSelectionChange) {
        // Pass the active object (could be single object or ActiveSelection)
        const activeObject = canvas.getActiveObject();
        onSelectionChange(true, activeObject || null);
      }
    });

    canvas.on("selection:cleared", () => {
      if (!isApplyingDataRef.current && onSelectionChange) {
        onSelectionChange(false, null);
      }
    });

    // Handle text editing events to trigger onChange
    canvas.on("text:changed", () => {
      if (!isApplyingDataRef.current && onChange) {
        onChange(getCanvasData());
      }
    });

    // Handle double-click to enter text editing mode (workaround for Fabric.js v7)
    canvas.on("mouse:dblclick", (e) => {
      if (readOnly) return;
      
      const target = e.target;
      if (target instanceof Textbox && target.editable !== false) {
        // Use setTimeout to ensure the click event is fully processed
        setTimeout(() => {
          if (!target.isEditing) {
            target.enterEditing();
            // Focus the hidden textarea that Fabric.js creates
            const hiddenTextarea = target.hiddenTextarea;
            if (hiddenTextarea) {
              hiddenTextarea.focus();
            }
          }
        }, 0);
      }
    });

    setIsReady(true);

    return () => {
      canvas.dispose();
      fabricRef.current = null;
      hasLoadedInitialDataRef.current = false;
    };
  }, [width, height, readOnly]);

  // Ref to track if canvas container is focused/active
  const canvasContainerRef = useRef<HTMLDivElement>(null);

  // Handle clipboard copy/paste events
  useEffect(() => {
    if (!isReady || !canvasRef.current || readOnly) return;

    const canvasContainer = canvasContainerRef.current;
    if (!canvasContainer) return;

    // Handle copy (Ctrl+C) - store selected object in internal clipboard
    const handleCopy = async (e: ClipboardEvent) => {
      const isCanvasActive = canvasContainer.contains(document.activeElement) || 
                             document.activeElement === canvasContainer ||
                             document.activeElement === canvasRef.current;
      
      if (!isCanvasActive) return;

      const canvas = fabricRef.current;
      if (!canvas) return;

      const activeObject = canvas.getActiveObject();
      if (!activeObject) return;

      // Don't intercept copy when editing text inside a textbox
      if (activeObject instanceof Textbox && (activeObject as any).isEditing) {
        return;
      }

      // Clone the object and store it in our internal clipboard
      try {
        const cloned = await activeObject.clone();
        copiedObjectRef.current = cloned;
        console.log('[ProposalCanvas] Object copied to internal clipboard');

        // Also serialize to localStorage for cross-template paste
        try {
          const elements: CanvasElement[] = [];
          if (activeObject instanceof ActiveSelection) {
            for (const obj of activeObject.getObjects()) {
              const serialized = serializeElement(obj);
              if (serialized) {
                // Compute absolute position within the selection
                const selectionMatrix = activeObject.calcTransformMatrix();
                const objLeft = obj.left || 0;
                const objTop = obj.top || 0;
                serialized.left = selectionMatrix[0] * objLeft + selectionMatrix[2] * objTop + selectionMatrix[4];
                serialized.top = selectionMatrix[1] * objLeft + selectionMatrix[3] * objTop + selectionMatrix[5];
                elements.push(serialized);
              }
            }
          } else {
            const serialized = serializeElement(activeObject);
            if (serialized) elements.push(serialized);
          }
          if (elements.length > 0) {
            localStorage.setItem("propale-clipboard", JSON.stringify({
              elements,
              timestamp: Date.now(),
            }));
            console.log('[ProposalCanvas] Object(s) serialized to localStorage for cross-template paste');
          }
        } catch (lsError) {
          console.warn('[ProposalCanvas] Failed to serialize to localStorage:', lsError);
        }

        // Override the system clipboard with a sentinel marker so that, on paste,
        // we can tell whether the most recent copy was an internal canvas copy
        // (use our internal clipboard) or an external copy (use system clipboard).
        e.clipboardData?.setData("text/plain", INTERNAL_CLIPBOARD_MARKER);

        // Prevent default to avoid browser putting a rendered image in clipboard
        e.preventDefault();
      } catch (error) {
        console.error('[ProposalCanvas] Failed to copy object:', error);
      }
    };

    const handlePaste = async (e: ClipboardEvent) => {
      // Check if canvas container or any of its children is active/focused
      const isCanvasActive = canvasContainer.contains(document.activeElement) || 
                             document.activeElement === canvasContainer ||
                             document.activeElement === canvasRef.current;
      
      console.log('[ProposalCanvas] Paste event triggered, isCanvasActive:', isCanvasActive, 'activeElement:', document.activeElement?.tagName);
      
      // Only handle paste when canvas area is active
      if (!isCanvasActive) {
        console.log('[ProposalCanvas] Ignoring paste - canvas not active');
        return;
      }

      const canvas = fabricRef.current;
      if (!canvas) return;

      // Check if user is currently editing a textbox (allow default paste behavior)
      const activeObject = canvas.getActiveObject();
      const isEditingText = activeObject instanceof Textbox && (activeObject as any).isEditing;

      // Determine whether the latest copy action was an internal canvas copy.
      // We only prefer the internal/localStorage clipboard when the system
      // clipboard still holds our sentinel marker; otherwise the user copied
      // external content (text/image) more recently and that should win.
      const clipboardText = e.clipboardData?.getData("text/plain") ?? "";
      const isInternalClipboard = clipboardText === INTERNAL_CLIPBOARD_MARKER;

      // Priority 1: If the last copy was internal and we have an object in the
      // internal clipboard (and not editing text), paste it
      if (isInternalClipboard && !isEditingText && copiedObjectRef.current) {
        e.preventDefault();
        try {
          const cloned = await copiedObjectRef.current.clone();
          // Offset slightly so it's visible as a new object
          cloned.set({
            left: (cloned.left || 0) + 20,
            top: (cloned.top || 0) + 20,
          });

          // Handle multi-selection
          if (copiedObjectRef.current instanceof ActiveSelection) {
            const clonedSelection = cloned as ActiveSelection;
            clonedSelection.canvas = canvas;
            clonedSelection.forEachObject((obj: FabricObject) => {
              canvas.add(obj);
            });
            clonedSelection.setCoords();
          } else {
            canvas.add(cloned);
          }

          canvas.setActiveObject(cloned);
          canvas.renderAll();
          console.log('[ProposalCanvas] Object pasted from internal clipboard');
          // Note: onChange is triggered automatically by the 'object:added' event listener
          return;
        } catch (error) {
          console.error('[ProposalCanvas] Failed to paste from internal clipboard:', error);
        }
      }

      // Priority 2: If last copy was internal but no in-memory object (e.g. after
      // a page reload), try localStorage (cross-template paste)
      if (isInternalClipboard && !isEditingText && !copiedObjectRef.current) {
        try {
          const stored = localStorage.getItem("propale-clipboard");
          if (stored) {
            const clipboardData = JSON.parse(stored) as { elements: CanvasElement[]; timestamp: number };
            if (clipboardData.elements && clipboardData.elements.length > 0) {
              e.preventDefault();
              const PASTE_OFFSET = 20;

              for (const element of clipboardData.elements) {
                const offsetElement = { ...element, left: (element.left || 0) + PASTE_OFFSET, top: (element.top || 0) + PASTE_OFFSET };
                await addElementToCanvas(offsetElement, false);
              }

              canvas.renderAll();
              console.log('[ProposalCanvas] Object(s) pasted from localStorage (cross-template)');
              return;
            }
          }
        } catch (lsError) {
          console.warn('[ProposalCanvas] Failed to paste from localStorage:', lsError);
        }

        // Last copy was internal but nothing usable to paste (e.g. localStorage
        // cleared). Stop here so we never paste the sentinel marker as text.
        e.preventDefault();
        return;
      }

      const items = e.clipboardData?.items;
      console.log('[ProposalCanvas] Clipboard items:', items?.length, 'types:', items ? Array.from(items).map(i => i.type) : []);
      if (!items) return;

      const itemsArray = Array.from(items);
      const clipboardTypes = itemsArray.map(item => item.type);
      
      // Detect if content comes from a rich text source (PowerPoint, Word, etc.)
      // These apps put text/html or text/rtf + text/plain + image in clipboard
      // In this case, we want the text, not the rendered image
      const hasRichText = clipboardTypes.some(type => type === 'text/html' || type === 'text/rtf');
      const plainText = e.clipboardData?.getData('text/plain')?.trim();
      const hasImage = clipboardTypes.some(type => type.startsWith('image/') && type !== 'image/svg+xml');
      
      // Consider it a rich text copy if:
      // 1. Has explicit rich text format (html/rtf) + text + image, OR
      // 2. Has meaningful text content (> 3 chars) + image (Office apps always include both)
      const hasMeaningfulText = plainText && plainText.length > 3;
      const isRichTextCopy = (hasRichText && plainText && hasImage) || (hasMeaningfulText && hasImage);
      
      console.log('[ProposalCanvas] Rich text detection:', { 
        clipboardTypes, 
        hasRichText, 
        hasPlainText: !!plainText, 
        plainTextLength: plainText?.length,
        hasImage, 
        isRichTextCopy 
      });

      // Helper function to extract text styles from HTML content
      const extractStylesFromHtml = (html: string): { fontSize?: number; fill?: string; fontFamily?: string; fontWeight?: string; fontStyle?: "normal" | "italic" } => {
        const styles: { fontSize?: number; fill?: string; fontFamily?: string; fontWeight?: string; fontStyle?: "normal" | "italic" } = {};
        
        try {
          // Create a temporary DOM element to parse HTML
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = DOMPurify.sanitize(html);
          
          // Find the first text-containing element with styles
          const findStyledElement = (element: Element): Element | null => {
            // Check if this element has text and styles
            if (element.textContent?.trim()) {
              const computedStyle = (element as HTMLElement).style;
              if (computedStyle.color || computedStyle.fontSize || computedStyle.fontFamily) {
                return element;
              }
            }
            // Recursively check children
            for (const child of Array.from(element.children)) {
              const found = findStyledElement(child);
              if (found) return found;
            }
            return null;
          };
          
          const styledElement = findStyledElement(tempDiv) || tempDiv.querySelector('span, p, div, font');
          
          if (styledElement) {
            const el = styledElement as HTMLElement;
            
            // Extract color
            const color = el.style.color || el.getAttribute('color');
            if (color) {
              // Convert rgb() to hex if needed
              if (color.startsWith('rgb')) {
                const match = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
                if (match) {
                  const r = parseInt(match[1]).toString(16).padStart(2, '0');
                  const g = parseInt(match[2]).toString(16).padStart(2, '0');
                  const b = parseInt(match[3]).toString(16).padStart(2, '0');
                  styles.fill = `#${r}${g}${b}`;
                }
              } else {
                styles.fill = color;
              }
            }
            
            // Extract font size
            const fontSize = el.style.fontSize;
            if (fontSize) {
              // Convert pt to px (1pt = 1.333px approximately)
              if (fontSize.endsWith('pt')) {
                styles.fontSize = Math.round(parseFloat(fontSize) * 1.333);
              } else if (fontSize.endsWith('px')) {
                styles.fontSize = Math.round(parseFloat(fontSize));
              }
            }
            
            // Extract font family
            const fontFamily = el.style.fontFamily;
            if (fontFamily) {
              // Clean up font family string (remove quotes)
              styles.fontFamily = fontFamily.replace(/['"]/g, '').split(',')[0].trim();
            }
            
            // Extract font weight
            const fontWeight = el.style.fontWeight;
            if (fontWeight === 'bold' || parseInt(fontWeight) >= 700) {
              styles.fontWeight = 'bold';
            }
            
            // Extract font style
            const fontStyleValue = el.style.fontStyle;
            if (fontStyleValue === 'italic') {
              styles.fontStyle = 'italic';
            }
          }
          
          console.log('[ProposalCanvas] Extracted styles from HTML:', styles);
        } catch (error) {
          console.error('[ProposalCanvas] Failed to extract styles from HTML:', error);
        }
        
        return styles;
      };

      // Helper function to add text to canvas
      const addTextToCanvas = (text: string, styles?: { fontSize?: number; fill?: string; fontFamily?: string; fontWeight?: string; fontStyle?: "normal" | "italic" }) => {
        console.log('[ProposalCanvas] Adding text to canvas:', text.substring(0, 50), 'with styles:', styles);
        e.preventDefault();
        
        const canvas = fabricRef.current;
        if (!canvas) return;

        const fontSize = styles?.fontSize || 16;
        const estimatedWidth = Math.min(Math.max(text.length * (fontSize * 0.6), 100), 600);
        const textbox = new Textbox(text, {
          left: (width - estimatedWidth) / 2,
          top: height / 2 - 50,
          fontSize: fontSize,
          fill: styles?.fill || "#000000",
          fontFamily: styles?.fontFamily || "Arial",
          fontWeight: styles?.fontWeight || "normal",
          fontStyle: styles?.fontStyle || "normal",
          width: estimatedWidth,
          selectable: true,
          editable: true,
          // activeOn: "up" fixes text editing in Fabric.js v7
          activeOn: "up",
        });

        canvas.add(textbox);
        canvas.setActiveObject(textbox);
        canvas.renderAll();
        // Note: onChange is triggered automatically by the 'object:added' event listener
      };

      // Helper function to add image to canvas
      const addImageToCanvas = (imageBlob: File) => {
        e.preventDefault();
        const reader = new FileReader();
        reader.onload = async (event) => {
          const dataUrl = event.target?.result as string;
          if (dataUrl) {
            const canvas = fabricRef.current;
            if (!canvas) return;

            try {
              const img = await FabricImage.fromURL(dataUrl);
              const imgWidth = img.width || 0;
              const imgHeight = img.height || 0;
              
              // Scale down if too large (max 80% of canvas size)
              const maxSize = Math.min(width, height) * 0.8;
              let scale = 1;
              if (imgWidth > maxSize || imgHeight > maxSize) {
                scale = Math.min(maxSize / imgWidth, maxSize / imgHeight);
                img.scale(scale);
              }
              
              // Calculate center position AFTER scaling
              const scaledWidth = imgWidth * scale;
              const scaledHeight = imgHeight * scale;
              img.set({
                left: (width - scaledWidth) / 2,
                top: (height - scaledHeight) / 2,
              });

              canvas.add(img);
              canvas.setActiveObject(img);
              canvas.renderAll();
              console.log('[ProposalCanvas] Image added to canvas successfully! Position:', img.left, img.top);
              // Note: onChange is triggered automatically by the 'object:added' event listener
            } catch (error) {
              console.error("[ProposalCanvas] Failed to paste image:", error);
            }
          }
        };
        reader.readAsDataURL(imageBlob);
      };

      // If rich text copy (from PowerPoint/Word), prefer text over image
      if (isRichTextCopy && !isEditingText && plainText) {
        console.log('[ProposalCanvas] Rich text detected - pasting as text instead of image');
        
        // Try to extract styles from HTML content
        const htmlContent = e.clipboardData?.getData('text/html');
        const styles = htmlContent ? extractStylesFromHtml(htmlContent) : undefined;
        
        addTextToCanvas(plainText, styles);
        return;
      }

      // Otherwise, check for images first (screenshots, actual images, etc.)
      let imageBlob: File | null = null;
      
      // Try PNG first, then other image formats
      for (const item of itemsArray) {
        if (item.type === 'image/png' || item.type === 'image/jpeg' || item.type === 'image/jpg') {
          const blob = item.getAsFile();
          if (blob) {
            imageBlob = blob;
            console.log('[ProposalCanvas] Found preferred image:', item.type, blob.size, 'bytes');
            break;
          }
        }
      }
      
      // If no PNG/JPEG found, try any other image format
      if (!imageBlob) {
        for (const item of itemsArray) {
          if (item.type.startsWith('image/') && item.type !== 'image/svg+xml') {
            const blob = item.getAsFile();
            if (blob) {
              imageBlob = blob;
              console.log('[ProposalCanvas] Found fallback image:', item.type, blob.size, 'bytes');
              break;
            }
          }
        }
      }

      if (imageBlob) {
        addImageToCanvas(imageBlob);
        return;
      }

      // Finally, process plain text (if not editing a textbox)
      console.log('[ProposalCanvas] Checking for text, isEditingText:', isEditingText);
      
      if (!isEditingText && plainText) {
        addTextToCanvas(plainText);
      }
    };

    // Listen for copy/paste at document level (these events don't bubble well on divs)
    document.addEventListener('copy', handleCopy);
    document.addEventListener('paste', handlePaste);

    return () => {
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('paste', handlePaste);
    };
  }, [isReady, readOnly, width, height]);

  // Load canvas data from JSON, with optional inherited data (locked objects rendered first)
  const loadCanvasData = useCallback(async (data?: CanvasData, inherited?: CanvasData) => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    isApplyingDataRef.current = true;
    try {
      canvas.clear();
      
      // Set background from inherited data first, then own data can override
      if (inherited) {
        canvas.backgroundColor = inherited.background || "#ffffff";
      }
      if (data?.background) {
        canvas.backgroundColor = data.background;
      } else if (!inherited) {
        canvas.backgroundColor = data?.background || "#ffffff";
      }

      // Load inherited objects first (locked, non-interactive)
      if (inherited) {
        for (const element of inherited.objects) {
          await addElementToCanvas(element, true);
        }
      }

      // Load own objects on top (fully interactive)
      if (data) {
        for (const element of data.objects) {
          await addElementToCanvas(element, false);
        }
      }

      canvas.renderAll();
    } finally {
      isApplyingDataRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [simulationOutputs, simulationInputs]);

  // Track simulation data to detect when it becomes available
  const prevSimulationDataRef = useRef<{ outputs: typeof simulationOutputs; inputs: typeof simulationInputs } | null>(null);

  // Load initial data or reload when simulation data becomes available
  useEffect(() => {
    if (!isReady || !fabricRef.current) return;
    if (!initialData && !inheritedData) return;
    
    // Check if simulation data just became available (changed from empty to non-empty)
    const prevOutputsEmpty = !prevSimulationDataRef.current?.outputs || Object.keys(prevSimulationDataRef.current.outputs).length === 0;
    const currentOutputsNotEmpty = Object.keys(simulationOutputs).length > 0;
    const simulationDataJustLoaded = prevOutputsEmpty && currentOutputsNotEmpty;
    
    // Update ref for next comparison
    prevSimulationDataRef.current = { outputs: simulationOutputs, inputs: simulationInputs };
    
    // Reload if simulation data just became available (to render placeholder values)
    if (simulationDataJustLoaded) {
      hasLoadedInitialDataRef.current = false;
    }
    
    // Only load initial data once to prevent reloading after local edits
    if (hasLoadedInitialDataRef.current) return;
    hasLoadedInitialDataRef.current = true;

    loadCanvasData(initialData, inheritedData);
  }, [isReady, initialData, inheritedData, loadCanvasData, simulationOutputs, simulationInputs]);

  // Helper function to format a value with prefix, suffix, decimal places, and thousand separator
  const formatValueWithOptions = (
    value: unknown,
    prefix?: string,
    suffix?: string,
    decimalPlaces?: number,
    useThousandSeparator?: boolean
  ): string => {
    if (value === undefined || value === null || value === "None") return "Aucun";
    
    let formattedValue: string;
    let numericValue: number | null = null;
    
    // Handle number formatting with decimal places
    if (typeof value === "number") {
      numericValue = value;
      if (decimalPlaces != null && decimalPlaces >= 0) {
        formattedValue = value.toFixed(decimalPlaces);
      } else {
        formattedValue = String(value);
      }
    } else if (typeof value === "string") {
      // Try to parse as number if decimal places is specified or thousand separator is enabled
      const parsedNum = parseFloat(value);
      if (!isNaN(parsedNum)) {
        numericValue = parsedNum;
        if (decimalPlaces != null && decimalPlaces >= 0) {
          formattedValue = parsedNum.toFixed(decimalPlaces);
        } else {
          // Use String(parsedNum) to clean up unnecessary decimals (e.g. "7.0" → "7", "7,0" → "7")
          formattedValue = String(parsedNum);
        }
      } else {
        formattedValue = value;
      }
    } else {
      formattedValue = String(value);
    }
    
    // Apply thousand separator (space) if enabled and value is numeric
    if (useThousandSeparator && numericValue !== null) {
      // Split by decimal point to handle integer and decimal parts separately
      const parts = formattedValue.split(/[.,]/);
      const integerPart = parts[0];
      const decimalPart = parts.length > 1 ? parts[1] : null;
      
      // Add space as thousand separator to integer part
      const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
      
      // Reconstruct with comma as decimal separator
      formattedValue = decimalPart !== null 
        ? `${formattedInteger},${decimalPart}` 
        : formattedInteger;
    } else if (numericValue !== null) {
      // Use comma as decimal separator (French convention)
      formattedValue = formattedValue.replace(".", ",");
    }
    
    // Apply prefix and suffix
    const prefixStr = prefix || "";
    const suffixStr = suffix || "";
    
    return `${prefixStr}${formattedValue}${suffixStr}`;
  };

  // Add element to canvas
  // When isInherited=true, the object is rendered as locked (non-selectable, non-interactive)
  const addElementToCanvas = async (element: CanvasElement, isInherited: boolean = false) => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    let fabricObject: FabricObject | null = null;

    switch (element.type) {
      case "textbox":
        fabricObject = new Textbox(element.text, {
          left: element.left,
          top: element.top,
          fontSize: element.fontSize || 16,
          fontWeight: element.fontWeight || "normal",
          fill: element.fill || "#000000",
          width: element.width || 200,
          textAlign: element.textAlign || "left",
          selectable: !readOnly,
          editable: !readOnly,
          // activeOn: "up" fixes text editing in Fabric.js v7
          activeOn: "up",
        });
        break;

      case "output_field":
        // Output fields are rendered as styled textboxes
        const displayValue = formatValueWithOptions(
          element.value,
          element.prefix,
          element.suffix,
          element.decimalPlaces,
          element.useThousandSeparator
        );
        fabricObject = new Textbox(displayValue, {
          left: element.left,
          top: element.top,
          width: element.width || 300, // Use provided width or default
          fontSize: element.fontSize || 14,
          fill: element.fill || "#0066B3", // laposte-blue
          fontWeight: element.fontWeight || "bold",
          fontFamily: element.fontFamily || "Arial",
          textAlign: element.textAlign || "center",
          padding: 6,
          selectable: !readOnly,
          editable: element.isEditable && !readOnly,
          // activeOn: "up" fixes text editing in Fabric.js v7
          activeOn: "up",
        });
        // Store custom properties
        fabricObject.set("customType", "output_field");
        fabricObject.set("outputSlug", element.outputSlug);
        fabricObject.set("originalValue", element.originalValue);
        fabricObject.set("isEditable", element.isEditable);
        fabricObject.set("prefix", element.prefix);
        fabricObject.set("suffix", element.suffix);
        fabricObject.set("decimalPlaces", element.decimalPlaces);
        fabricObject.set("useThousandSeparator", element.useThousandSeparator);
        break;

      case "input_field":
        // Input fields are rendered as styled textboxes (green color scheme)
        const inputDisplayValue = formatValueWithOptions(
          element.value,
          element.prefix,
          element.suffix,
          element.decimalPlaces,
          element.useThousandSeparator
        );
        fabricObject = new Textbox(inputDisplayValue, {
          left: element.left,
          top: element.top,
          width: element.width || 300,
          fontSize: element.fontSize || 14,
          fill: element.fill || "#00B2A9", // logissimo teal
          fontWeight: element.fontWeight || "bold",
          fontFamily: element.fontFamily || "Arial",
          textAlign: element.textAlign || "center",
          padding: 6,
          selectable: !readOnly,
          editable: false, // Input fields are not editable
          // activeOn: "up" fixes text editing in Fabric.js v7
          activeOn: "up",
        });
        // Store custom properties
        fabricObject.set("customType", "input_field");
        fabricObject.set("inputSlug", element.inputSlug);
        fabricObject.set("prefix", element.prefix);
        fabricObject.set("suffix", element.suffix);
        fabricObject.set("decimalPlaces", element.decimalPlaces);
        fabricObject.set("useThousandSeparator", element.useThousandSeparator);
        break;

      case "image":
        try {
          const img = await FabricImage.fromURL(element.src);
          img.set({
            left: element.left,
            top: element.top,
            scaleX: element.scaleX || 1,
            scaleY: element.scaleY || 1,
            selectable: !readOnly,
          });
          fabricObject = img;
        } catch (error) {
          console.error("Failed to load image:", error);
        }
        break;

      case "rect":
        fabricObject = new Rect({
          left: element.left,
          top: element.top,
          width: element.width || 100,
          height: element.height || 100,
          fill: element.fill || "#e5e5e5",
          stroke: element.stroke,
          strokeWidth: element.strokeWidth,
          selectable: !readOnly,
        });
        break;

      case "circle":
        fabricObject = new Circle({
          left: element.left,
          top: element.top,
          radius: element.radius || 50,
          fill: element.fill || "#e5e5e5",
          stroke: element.stroke,
          strokeWidth: element.strokeWidth,
          selectable: !readOnly,
        });
        break;

      case "triangle":
        fabricObject = new Triangle({
          left: element.left,
          top: element.top,
          width: element.width || 100,
          height: element.height || 100,
          fill: element.fill || "#e5e5e5",
          stroke: element.stroke,
          strokeWidth: element.strokeWidth,
          selectable: !readOnly,
        });
        break;

      case "line":
        fabricObject = new Line([element.left, element.top, element.left + (element.width || 100), element.top], {
          stroke: element.stroke || "#000000",
          strokeWidth: element.strokeWidth || 2,
          selectable: !readOnly,
        });
        break;

      case "table":
        // Tables are rendered as a Fabric.js Group of cell rects + text
        addTableToCanvas(
          canvas,
          {
            rows: element.rows,
            cols: element.cols,
            data: element.data || [],
            left: element.left,
            top: element.top,
            width: element.width || 300,
            height: element.height || 150,
            headerBgColor: element.headerBgColor,
            borderColor: element.borderColor,
            hasHeader: element.hasHeader,
            hasRowHeader: element.hasRowHeader,
            skipHideZeroRows: element.skipHideZeroRows,
            fontSize: element.fontSize,
            fontFamily: element.fontFamily,
            fill: element.fill,
            headerTextColor: element.headerTextColor,
          },
          readOnly,
          isInherited,
        );
        return; // addTableToCanvas handles adding objects

      case "placeholder_zone":
        // Placeholder zones are dashed rectangles that display values from simulation
        // Use different colors based on placeholder type:
        // - pricing_grid and output_value: blue (#0066B3)
        // - input_value: green (#00B2A9)
        // - data_table: purple (#9B5DE5)
        const isInputZone = element.placeholder_type === "input_value";
        const isDataTablePlaceholder = element.placeholder_type === "data_table";
        const zoneColor = isDataTablePlaceholder ? "#9B5DE5" : isInputZone ? "#00B2A9" : "#0066B3";
        const zoneFill = isDataTablePlaceholder ? "rgba(155, 93, 229, 0.08)" : isInputZone ? "rgba(0, 178, 169, 0.08)" : "rgba(0, 102, 179, 0.08)";
        const zoneWidth = element.width || 300;
        const zoneHeight = element.height || 200;
        
        // Get the slug and look up the value from simulation data
        const valueSlug = isInputZone ? element.input_slug : element.output_slug;
        const rawValue = isInputZone 
          ? (valueSlug ? simulationInputs[valueSlug] : undefined)
          : (valueSlug ? simulationOutputs[valueSlug] : undefined);
        
        // Format the value with the configured options
        const formattedValue = formatValueWithOptions(
          rawValue,
          element.prefix,
          element.suffix,
          element.decimalPlaces,
          element.useThousandSeparator
        );
        
        // Check if we have an actual value to display
        const hasValue = rawValue !== undefined && rawValue !== null && formattedValue !== "-";
        
        fabricObject = new Rect({
          left: element.left,
          top: element.top,
          width: zoneWidth,
          height: zoneHeight,
          fill: hasValue ? "transparent" : zoneFill, // Hide background when value is shown
          stroke: hasValue ? "transparent" : zoneColor, // Hide border when value is shown
          strokeWidth: 2,
          strokeDashArray: [8, 4],
          selectable: !readOnly,
        });
        fabricObject.set("customType", "placeholder_zone");
        fabricObject.set("placeholderType", element.placeholder_type);
        fabricObject.set("outputSlug", element.output_slug);
        fabricObject.set("inputSlug", element.input_slug);
        const defaultLabel = element.placeholder_type === "pricing_grid" 
          ? "Grille Tarifaire" 
          : element.placeholder_type === "data_table"
            ? "Tableau de donnees"
            : element.placeholder_type === "input_value" 
              ? "Valeur Input" 
              : "Valeur";
        fabricObject.set("placeholderLabel", element.label || defaultLabel);
        // Value formatting options
        fabricObject.set("prefix", element.prefix);
        fabricObject.set("suffix", element.suffix);
        fabricObject.set("decimalPlaces", element.decimalPlaces);
        fabricObject.set("useThousandSeparator", element.useThousandSeparator);
        // Text styling options
        fabricObject.set("fontSize", element.fontSize);
        fabricObject.set("fill", element.fill);
        fabricObject.set("fontFamily", element.fontFamily);
        fabricObject.set("fontWeight", element.fontWeight);
        fabricObject.set("textAlign", element.textAlign);
        // Data table columns
        if (element.table_columns) {
          fabricObject.set("tableColumns", element.table_columns);
        }
        
        // Lock inherited objects
        if (isInherited) {
          fabricObject.set({
            selectable: false,
            evented: false,
            hasControls: false,
            hasBorders: false,
            lockMovementX: true,
            lockMovementY: true,
            hoverCursor: "default",
            customIsInherited: true,
          });
        }
        
        // Add the rectangle to the canvas
        canvas.add(fabricObject);
        
        // Return early since we already added the object
        return;

      case "pricing_grid_table":
        // Pricing grid table - render as structured table with groups and fields
        await addPricingGridToCanvas(canvas, element, readOnly || isInherited);
        // Mark pricing grid sub-objects as inherited if needed
        if (isInherited) {
          canvas.getObjects().forEach((obj) => {
            if (!obj.customIsInherited && (obj.customType === "pricing_grid_table" || obj.customType === "pricing_grid_bg")) {
              obj.set({
                selectable: false,
                evented: false,
                hasControls: false,
                hasBorders: false,
                lockMovementX: true,
                lockMovementY: true,
                hoverCursor: "default",
                customIsInherited: true,
              });
            }
          });
        }
        return; // Don't add fabricObject - addPricingGridToCanvas handles adding objects
    }

    if (fabricObject) {
      // Lock inherited objects
      if (isInherited) {
        fabricObject.set({
          selectable: false,
          evented: false,
          hasControls: false,
          hasBorders: false,
          lockMovementX: true,
          lockMovementY: true,
          hoverCursor: "default",
          customIsInherited: true,
        });
      }
      canvas.add(fabricObject);
    }
  };

  // Helper function to check if a value is zero/empty (should be filtered out)
  const isZeroValue = (value: unknown): boolean => {
    if (value === null || value === undefined) return true;
    if (typeof value === "number") return value === 0;
    if (typeof value === "string") {
      const trimmed = value.trim();
      return trimmed === "" || trimmed === "0" || trimmed === "0.0" || trimmed === "0,0" || 
             trimmed === "0.00" || trimmed === "0,00" || trimmed === "-" ||
             trimmed === "0.00 EUR" || trimmed === "0,00 EUR" || trimmed === "0 EUR" ||
             trimmed === "0.00%" || trimmed === "0,00%" || trimmed === "0%";
    }
    return false;
  };

  // Helper: get display text for a table cell value
  const getCellDisplayText = (cell: TableCellValue): string => {
    if (typeof cell === "string") return cell;
    if (cell && typeof cell === "object" && cell.slug) {
      const prefix = cell.type === "output" ? "↗ " : "↙ ";
      return `${prefix}${cell.slug}`;
    }
    return "";
  };


  // Helper: determine if a cell is a slug reference
  const isCellRef = (cell: TableCellValue): cell is { type: "output" | "input"; slug: string } => {
    return typeof cell === "object" && cell !== null && "slug" in cell;
  };

  // Helper function to render a static table on the canvas as a Fabric.js Group
  const addTableToCanvas = (
    canvas: Canvas,
    config: {
      rows: number;
      cols: number;
      data: TableCellValue[][];
      left: number;
      top: number;
      width: number;
      height: number;
      headerBgColor?: string;
      borderColor?: string;
      hasHeader?: boolean;
      hasRowHeader?: boolean;
      skipHideZeroRows?: boolean;
      fontSize?: number;
      fontFamily?: string;
      fill?: string;
      headerTextColor?: string;
    },
    isReadOnly: boolean = false,
    isInherited: boolean = false,
  ) => {
    const {
      rows,
      cols,
      data,
      left,
      top,
      width,
      height,
      headerBgColor = "#003366",
      borderColor = "#dee2e6",
      hasHeader = true,
      hasRowHeader = false,
      skipHideZeroRows = false,
      fontSize = 12,
      fontFamily = "Arial",
      fill = "#000000",
      headerTextColor = "#ffffff",
    } = config;

    const cellWidth = width / cols;
    const cellHeight = height / rows;
    const CELL_PADDING = 6;

    const objects: FabricObject[] = [];

    for (let r = 0; r < rows; r++) {
      const isHeaderRow = hasHeader && r === 0;
      for (let c = 0; c < cols; c++) {
        const isHeaderCol = hasRowHeader && c === 0;
        const isAnyHeader = isHeaderRow || isHeaderCol;
        const cellRaw = data[r]?.[c] ?? "";
        const isRef = isCellRef(cellRaw);

        // Cell background
        let cellBg: string;
        if (isAnyHeader) {
          cellBg = headerBgColor;
        } else if (isRef) {
          cellBg = cellRaw.type === "output" ? "rgba(0, 102, 179, 0.06)" : "rgba(0, 178, 169, 0.06)";
        } else {
          cellBg = r % 2 === 0 ? "#ffffff" : "#f9fafb";
        }

        const cellRect = new Rect({
          left: c * cellWidth,
          top: r * cellHeight,
          width: cellWidth,
          height: cellHeight,
          fill: cellBg,
          stroke: borderColor,
          strokeWidth: 1,
        });
        objects.push(cellRect);

        // Cell text
        const displayText = getCellDisplayText(cellRaw);
        const textColor = isAnyHeader
          ? headerTextColor
          : isRef
            ? (cellRaw.type === "output" ? "#0066B3" : "#00B2A9")
            : fill;

        const cellText = new Textbox(displayText, {
          left: c * cellWidth + CELL_PADDING,
          top: r * cellHeight,
          textAlign: "center",
          lineHeight: 1,
          fontSize: isRef ? Math.max(fontSize - 1, 8) : fontSize,
          fontFamily: isRef ? "monospace" : fontFamily,
          fontWeight: isAnyHeader ? "bold" : "normal",
          fontStyle: isRef ? "italic" : "normal",
          fill: textColor,
          width: cellWidth - CELL_PADDING * 2,
          splitByGrapheme: false,
          editable: false,
        });
        const textHeight = cellText.calcTextHeight();
        const clampedHeight = Math.min(textHeight, cellHeight);
        cellText.set({
          height: clampedHeight,
          top: r * cellHeight + Math.max(0, (cellHeight - clampedHeight) / 2),
        });
        (cellText as any).clipPath = undefined;
        objects.push(cellText);

        if (isRef && cellRaw.visibilityConditions && cellRaw.visibilityConditions.conditions?.length > 0) {
          const dot = new Circle({
            left: (c + 1) * cellWidth - 7,
            top: r * cellHeight + 2,
            radius: 3,
            fill: "#f59e0b",
            stroke: "#d97706",
            strokeWidth: 0.5,
          });
          objects.push(dot);
        }
      }
    }

    const group = new Group(objects, {
      left: left,
      top: top,
      selectable: !isReadOnly,
      subTargetCheck: false,
      interactive: false,
    });

    group.set("customType", "table");
    group.set("tableRows", rows);
    group.set("tableCols", cols);
    group.set("tableData", data);
    group.set("tableHeaderBgColor", headerBgColor);
    group.set("tableBorderColor", borderColor);
    group.set("tableHasHeader", hasHeader);
    group.set("tableHasRowHeader", hasRowHeader);
    group.set("tableSkipHideZeroRows", skipHideZeroRows);
    group.set("tableFontSize", fontSize);
    group.set("tableFontFamily", fontFamily);
    group.set("tableFill", fill);
    group.set("tableHeaderTextColor", headerTextColor);
    group.set("tableWidth", width);
    group.set("tableHeight", height);

    if (isInherited) {
      group.set({
        selectable: false,
        evented: false,
        hasControls: false,
        hasBorders: false,
        lockMovementX: true,
        lockMovementY: true,
        hoverCursor: "default",
        customIsInherited: true,
      });
    }

    canvas.add(group);
    return group;
  };

  // Helper function to render a pricing grid table on the canvas
  const addPricingGridToCanvas = async (
    canvas: Canvas,
    element: CanvasElement & { type: "pricing_grid_table" },
    readOnly: boolean
  ) => {
    const { left, top, width, groups } = element;
    const ROW_HEIGHT = 28;
    const GROUP_HEADER_HEIGHT = 32;
    const PADDING = 10;
    const LABEL_WIDTH_RATIO = 0.7; // 70% for label, 30% for value

    // Filter out zero/empty values from groups
    const filteredGroups = groups.map(group => ({
      ...group,
      fields: group.fields.filter(field => !isZeroValue(field.rawValue) && !isZeroValue(field.value))
    })).filter(group => group.fields.length > 0);

    let currentY = top;

    // Calculate actual height based on content
    let totalHeight = 0;
    for (const group of filteredGroups) {
      totalHeight += GROUP_HEADER_HEIGHT;
      totalHeight += group.fields.length * ROW_HEIGHT;
      totalHeight += 4; // spacing
    }
    const actualHeight = Math.max(totalHeight, 50);

    // Add main container background
    const containerBg = new Rect({
      left: left,
      top: top,
      width: width,
      height: actualHeight,
      fill: "#ffffff",
      stroke: "#dee2e6",
      strokeWidth: 1,
      rx: 4,
      ry: 4,
      selectable: false,
      evented: false,
    });
    containerBg.set("customType", "pricing_grid_bg");
    canvas.add(containerBg);

    for (const group of filteredGroups) {
      // Add group header background
      const headerBg = new Rect({
        left: left,
        top: currentY,
        width: width,
        height: GROUP_HEADER_HEIGHT,
        fill: group.backgroundColor || "#f0f4f8",
        selectable: false,
        evented: false,
      });
      canvas.add(headerBg);

      // Add group header text
      const headerText = new Textbox(group.name, {
        left: left + PADDING,
        top: currentY + 6,
        width: width - PADDING * 2,
        fontSize: 12,
        fontWeight: "600",
        fill: "#374151",
        selectable: false,
        editable: false,
      });
      canvas.add(headerText);

      currentY += GROUP_HEADER_HEIGHT;

      // Add field rows
      for (let i = 0; i < group.fields.length; i++) {
        const field = group.fields[i];
        const isEven = i % 2 === 0;

        // Row background (alternate colors)
        const rowBg = new Rect({
          left: left,
          top: currentY,
          width: width,
          height: ROW_HEIGHT,
          fill: isEven ? "#ffffff" : "#f9fafb",
          selectable: false,
          evented: false,
        });
        canvas.add(rowBg);

        // Field label
        const labelText = new Textbox(field.label, {
          left: left + PADDING,
          top: currentY + 5,
          width: width * LABEL_WIDTH_RATIO - PADDING * 2,
          fontSize: 11,
          fill: "#4b5563",
          selectable: false,
          editable: false,
        });
        canvas.add(labelText);

        // Field value (right aligned, styled like output_field)
        const valueText = new Textbox(field.value, {
          left: left + width * LABEL_WIDTH_RATIO,
          top: currentY + 5,
          width: width * (1 - LABEL_WIDTH_RATIO) - PADDING,
          fontSize: 11,
          fontWeight: "bold",
          fill: "#0066B3", // La Poste blue
          textAlign: "right",
          selectable: false,
          editable: false,
        });
        canvas.add(valueText);

        currentY += ROW_HEIGHT;
      }

      // Add spacing between groups
      currentY += 4;
    }

    // Store element data for serialization (on the container background)
    // Store filtered groups so data stays clean
    containerBg.set("customType", "pricing_grid_table");
    (containerBg as any).pricingGridData = {
      groups: filteredGroups,
      width: width,
      height: actualHeight,
    };
  };

  // Serialize a single FabricObject into a portable CanvasElement JSON.
  // Used for cross-template clipboard persistence via localStorage.
  const serializeElement = useCallback((obj: FabricObject): CanvasElement | null => {
    const scaleX = obj.scaleX || 1;
    const scaleY = obj.scaleY || 1;
    const objWidth = (obj.width || 0) * scaleX;
    const objHeight = (obj.height || 0) * scaleY;
    const left = obj.left || 0;
    const top = obj.top || 0;

    if (obj.customType === "output_field" && obj instanceof Textbox) {
      return {
        type: "output_field",
        outputSlug: obj.outputSlug || "",
        value: obj.text,
        originalValue: obj.originalValue,
        isEditable: obj.isEditable || false,
        fontSize: obj.fontSize,
        fill: obj.fill as string,
        fontWeight: obj.fontWeight as string,
        fontFamily: obj.fontFamily,
        textAlign: obj.textAlign as "left" | "center" | "right" | "justify",
        width: objWidth,
        height: objHeight,
        left,
        top,
        prefix: obj.prefix,
        suffix: obj.suffix,
        decimalPlaces: obj.decimalPlaces,
        useThousandSeparator: obj.useThousandSeparator,
      };
    } else if (obj.customType === "input_field" && obj instanceof Textbox) {
      return {
        type: "input_field",
        inputSlug: obj.inputSlug || "",
        value: obj.text,
        fontSize: obj.fontSize,
        fill: obj.fill as string,
        fontWeight: obj.fontWeight as string,
        fontFamily: obj.fontFamily,
        textAlign: obj.textAlign as "left" | "center" | "right" | "justify",
        width: objWidth,
        height: objHeight,
        left,
        top,
        prefix: obj.prefix,
        suffix: obj.suffix,
        decimalPlaces: obj.decimalPlaces,
        useThousandSeparator: obj.useThousandSeparator,
      };
    } else if (obj instanceof Textbox) {
      return {
        type: "textbox",
        text: obj.text || "",
        fontSize: obj.fontSize,
        fontWeight: obj.fontWeight as string,
        fill: obj.fill as string,
        textAlign: obj.textAlign as "left" | "center" | "right" | "justify",
        width: objWidth,
        height: objHeight,
        left,
        top,
      };
    } else if (obj instanceof Rect) {
      if (obj.customType === "placeholder_zone") {
        return {
          type: "placeholder_zone",
          placeholder_type: obj.placeholderType || "pricing_grid",
          output_slug: obj.outputSlug,
          input_slug: obj.inputSlug,
          label: obj.placeholderLabel,
          width: objWidth || 300,
          height: objHeight || 200,
          left,
          top,
          prefix: obj.prefix,
          suffix: obj.suffix,
          decimalPlaces: obj.decimalPlaces,
          useThousandSeparator: obj.useThousandSeparator,
          fontSize: obj.fontSize,
          fill: obj.fill as string | undefined,
          fontFamily: obj.fontFamily,
          fontWeight: obj.fontWeight as string | undefined,
          textAlign: obj.textAlign as "left" | "center" | "right" | "justify" | undefined,
          table_columns: obj.tableColumns,
        };
      } else if (obj.customType === "pricing_grid_table") {
        const pricingGridData = (obj as any).pricingGridData;
        if (pricingGridData) {
          return {
            type: "pricing_grid_table",
            groups: pricingGridData.groups || [],
            width: pricingGridData.width || objWidth || 400,
            height: pricingGridData.height || objHeight || 200,
            left,
            top,
          };
        }
        return null;
      } else if (obj.customType === "pricing_grid_bg") {
        return null;
      } else {
        return {
          type: "rect",
          width: objWidth,
          height: objHeight,
          fill: obj.fill as string,
          stroke: obj.stroke as string,
          strokeWidth: obj.strokeWidth,
          left,
          top,
        };
      }
    } else if (obj instanceof Circle) {
      const scaledRadius = Math.min(objWidth, objHeight) / 2;
      return {
        type: "circle",
        radius: scaledRadius,
        fill: obj.fill as string,
        stroke: obj.stroke as string,
        strokeWidth: obj.strokeWidth,
        left,
        top,
      };
    } else if (obj instanceof Triangle) {
      return {
        type: "triangle",
        width: objWidth,
        height: objHeight,
        fill: obj.fill as string,
        stroke: obj.stroke as string,
        strokeWidth: obj.strokeWidth,
        left,
        top,
      };
    } else if (obj instanceof Line) {
      return {
        type: "line",
        width: objWidth,
        stroke: obj.stroke as string,
        strokeWidth: obj.strokeWidth,
        left,
        top,
      };
    } else if (obj instanceof FabricImage) {
      return {
        type: "image",
        src: obj.getSrc(),
        width: obj.width || objWidth,
        height: obj.height || objHeight,
        scaleX: obj.scaleX,
        scaleY: obj.scaleY,
        left,
        top,
      };
    } else if (obj instanceof Group && obj.customType === "table") {
      return {
        type: "table",
        rows: obj.tableRows || 3,
        cols: obj.tableCols || 3,
        data: obj.tableData || [],
        width: obj.tableWidth || objWidth,
        height: obj.tableHeight || objHeight,
        left,
        top,
        headerBgColor: obj.tableHeaderBgColor,
        borderColor: obj.tableBorderColor,
        hasHeader: obj.tableHasHeader,
        hasRowHeader: obj.tableHasRowHeader,
        skipHideZeroRows: obj.tableSkipHideZeroRows,
        fontSize: obj.tableFontSize,
        fontFamily: obj.tableFontFamily,
        fill: obj.tableFill,
        headerTextColor: obj.tableHeaderTextColor,
      };
    }

    return null;
  }, []);

  // Get canvas data as JSON
  // @param forExport - If true, uses visual coordinates (aCoords.tl) for PPTX export.
  //                    If false (default), uses Fabric.js properties for saving/reloading.
  const getCanvasData = useCallback((forExport: boolean = false): CanvasData => {
    const canvas = fabricRef.current;
    if (!canvas) {
      return { version: "5.3.0", objects: [], background: "#ffffff" };
    }

    // Handle ActiveSelection (multi-selection): when objects are in a group,
    // their coordinates are relative to the group center. We calculate
    // absolute coordinates using transformation matrices instead of
    // discarding/recreating the selection (which can cause UI bugs).
    const activeObject = canvas.getActiveObject();
    const isMultiSelection = activeObject instanceof ActiveSelection;
    
    // Get list of objects in the active selection for coordinate transformation
    const selectedObjects = isMultiSelection ? activeObject.getObjects() : [];

    // Helper function to get absolute coordinates and dimensions for an object
    // Two modes:
    // - forExport=true: Uses aCoords.tl for visual coordinates (needed for PPTX export)
    // - forExport=false: Uses obj.left/top for Fabric.js compatibility (saving/reloading)
    const getAbsoluteBounds = (obj: FabricObject): { left: number; top: number; width: number; height: number } => {
      const scaleX = obj.scaleX || 1;
      const scaleY = obj.scaleY || 1;
      const objWidth = (obj.width || 0) * scaleX;
      const objHeight = (obj.height || 0) * scaleY;
      
      if (isMultiSelection && selectedObjects.includes(obj)) {
        // Object is part of ActiveSelection - calculate absolute position
        // using the selection's transformation matrix
        const selectionMatrix = activeObject.calcTransformMatrix();
        
        if (forExport) {
          // For export: use center point to get accurate visual position
          const objectCenter = obj.getCenterPoint();
          const absoluteCenter = {
            x: selectionMatrix[0] * objectCenter.x + selectionMatrix[2] * objectCenter.y + selectionMatrix[4],
            y: selectionMatrix[1] * objectCenter.x + selectionMatrix[3] * objectCenter.y + selectionMatrix[5],
          };
          return {
            left: absoluteCenter.x - objWidth / 2,
            top: absoluteCenter.y - objHeight / 2,
            width: objWidth,
            height: objHeight,
          };
        } else {
          // For saving: transform the object's left/top through the selection matrix
          const objLeft = obj.left || 0;
          const objTop = obj.top || 0;
          const absoluteLeft = selectionMatrix[0] * objLeft + selectionMatrix[2] * objTop + selectionMatrix[4];
          const absoluteTop = selectionMatrix[1] * objLeft + selectionMatrix[3] * objTop + selectionMatrix[5];
          return {
            left: absoluteLeft,
            top: absoluteTop,
            width: objWidth,
            height: objHeight,
          };
        }
      }
      
      if (forExport) {
        // For export: use aCoords.tl which gives absolute top-left visual coordinates
        // This correctly handles objects regardless of their originX/originY
        obj.setCoords();
        if (obj.aCoords && obj.aCoords.tl) {
          return {
            left: obj.aCoords.tl.x,
            top: obj.aCoords.tl.y,
            width: objWidth,
            height: objHeight,
          };
        }
      }
      
      // Default: use direct properties for Fabric.js compatibility
      return {
        left: obj.left || 0,
        top: obj.top || 0,
        width: objWidth,
        height: objHeight,
      };
    };

    const objects: CanvasElement[] = [];

    canvas.getObjects().forEach((obj) => {
      // Skip inherited (locked) objects - they belong to the parent template
      if (obj.customIsInherited) return;

      if (obj.customType === "output_field" && obj instanceof Textbox) {
        // Use bounding rect for accurate position and dimensions (includes scale)
        const bounds = getAbsoluteBounds(obj);
        objects.push({
          type: "output_field",
          outputSlug: obj.outputSlug || "",
          value: obj.text,
          originalValue: obj.originalValue,
          isEditable: obj.isEditable || false,
          fontSize: obj.fontSize,
          fill: obj.fill as string,
          fontWeight: obj.fontWeight as string,
          fontFamily: obj.fontFamily,
          textAlign: obj.textAlign as "left" | "center" | "right" | "justify",
          width: bounds.width,
          height: bounds.height,
          left: bounds.left,
          top: bounds.top,
          prefix: obj.prefix,
          suffix: obj.suffix,
          decimalPlaces: obj.decimalPlaces,
          useThousandSeparator: obj.useThousandSeparator,
        });
      } else if (obj.customType === "input_field" && obj instanceof Textbox) {
        // Input fields - similar to output fields but with inputSlug
        const bounds = getAbsoluteBounds(obj);
        objects.push({
          type: "input_field",
          inputSlug: obj.inputSlug || "",
          value: obj.text,
          fontSize: obj.fontSize,
          fill: obj.fill as string,
          fontWeight: obj.fontWeight as string,
          fontFamily: obj.fontFamily,
          textAlign: obj.textAlign as "left" | "center" | "right" | "justify",
          width: bounds.width,
          height: bounds.height,
          left: bounds.left,
          top: bounds.top,
          prefix: obj.prefix,
          suffix: obj.suffix,
          decimalPlaces: obj.decimalPlaces,
          useThousandSeparator: obj.useThousandSeparator,
        });
      } else if (obj instanceof Textbox) {
        // Use bounding rect for accurate position and dimensions (includes scale)
        const bounds = getAbsoluteBounds(obj);
        objects.push({
          type: "textbox",
          text: obj.text || "",
          fontSize: obj.fontSize,
          fontWeight: obj.fontWeight as string,
          fill: obj.fill as string,
          textAlign: obj.textAlign as "left" | "center" | "right" | "justify",
          width: bounds.width,
          height: bounds.height,
          left: bounds.left,
          top: bounds.top,
        });
      } else if (obj instanceof Rect) {
        // Get bounding rect for accurate dimensions (includes scale)
        const bounds = getAbsoluteBounds(obj);
        
        if (obj.customType === "placeholder_zone") {
          objects.push({
            type: "placeholder_zone",
            placeholder_type: obj.placeholderType || "pricing_grid",
            output_slug: obj.outputSlug,
            input_slug: obj.inputSlug,
            label: obj.placeholderLabel,
            width: bounds.width || 300,
            height: bounds.height || 200,
            left: bounds.left,
            top: bounds.top,
            // Value formatting options
            prefix: obj.prefix,
            suffix: obj.suffix,
            decimalPlaces: obj.decimalPlaces,
            useThousandSeparator: obj.useThousandSeparator,
            // Text styling options
            fontSize: obj.fontSize,
            fill: obj.fill as string | undefined,
            fontFamily: obj.fontFamily,
            fontWeight: obj.fontWeight as string | undefined,
            textAlign: obj.textAlign as "left" | "center" | "right" | "justify" | undefined,
            // Data table columns
            table_columns: obj.tableColumns,
          });
        } else if (obj.customType === "pricing_grid_table") {
          // Serialize pricing grid table
          const pricingGridData = (obj as any).pricingGridData;
          if (pricingGridData) {
            objects.push({
              type: "pricing_grid_table",
              groups: pricingGridData.groups || [],
              width: pricingGridData.width || bounds.width || 400,
              height: pricingGridData.height || bounds.height || 200,
              left: bounds.left,
              top: bounds.top,
            });
          }
        } else if (obj.customType === "pricing_grid_bg") {
          // Skip background elements of pricing grid - they're part of pricing_grid_table
        } else {
          // Regular rect - use bounding rect dimensions (includes scale)
          objects.push({
            type: "rect",
            width: bounds.width,
            height: bounds.height,
            fill: obj.fill as string,
            stroke: obj.stroke as string,
            strokeWidth: obj.strokeWidth,
            left: bounds.left,
            top: bounds.top,
          });
        }
      } else if (obj instanceof Circle) {
        // Get bounding rect for accurate dimensions (includes scale)
        const bounds = getAbsoluteBounds(obj);
        // For circles, calculate the scaled radius from bounding rect
        // The bounding rect width/height should be equal (diameter)
        const scaledRadius = Math.min(bounds.width, bounds.height) / 2;
        objects.push({
          type: "circle",
          radius: scaledRadius,
          fill: obj.fill as string,
          stroke: obj.stroke as string,
          strokeWidth: obj.strokeWidth,
          left: bounds.left,
          top: bounds.top,
        });
      } else if (obj instanceof Triangle) {
        // Get bounding rect for accurate dimensions (includes scale)
        const bounds = getAbsoluteBounds(obj);
        objects.push({
          type: "triangle",
          width: bounds.width,
          height: bounds.height,
          fill: obj.fill as string,
          stroke: obj.stroke as string,
          strokeWidth: obj.strokeWidth,
          left: bounds.left,
          top: bounds.top,
        });
      } else if (obj instanceof Line) {
        // Get bounding rect for accurate dimensions (includes scale)
        const bounds = getAbsoluteBounds(obj);
        objects.push({
          type: "line",
          width: bounds.width,
          stroke: obj.stroke as string,
          strokeWidth: obj.strokeWidth,
          left: bounds.left,
          top: bounds.top,
        });
      } else if (obj instanceof FabricImage) {
        // Use bounding rect for accurate position and dimensions (includes scale)
        const bounds = getAbsoluteBounds(obj);
        // Export original dimensions with scale factors for backend compatibility
        // Backend will multiply width/height by scaleX/scaleY
        objects.push({
          type: "image",
          src: obj.getSrc(),
          width: obj.width || bounds.width,
          height: obj.height || bounds.height,
          scaleX: obj.scaleX,
          scaleY: obj.scaleY,
          left: bounds.left,
          top: bounds.top,
        });
      } else if (obj instanceof Group && obj.customType === "table") {
        // Serialize table Group with all metadata
        const bounds = getAbsoluteBounds(obj);
        objects.push({
          type: "table",
          rows: obj.tableRows || 3,
          cols: obj.tableCols || 3,
          data: obj.tableData || [],
          width: obj.tableWidth || bounds.width,
          height: obj.tableHeight || bounds.height,
          left: bounds.left,
          top: bounds.top,
          headerBgColor: obj.tableHeaderBgColor,
          borderColor: obj.tableBorderColor,
          hasHeader: obj.tableHasHeader,
          hasRowHeader: obj.tableHasRowHeader,
          skipHideZeroRows: obj.tableSkipHideZeroRows,
          fontSize: obj.tableFontSize,
          fontFamily: obj.tableFontFamily,
          fill: obj.tableFill,
          headerTextColor: obj.tableHeaderTextColor,
        });
      }
    });

    return {
      version: "5.3.0",
      objects,
      background: canvas.backgroundColor as string,
    };
  }, []);

  // Public methods exposed via ref or callbacks
  const addText = useCallback((text: string = "Nouveau texte") => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    const textbox = new Textbox(text, {
      left: 100,
      top: 100,
      fontSize: 16,
      fill: "#000000",
      width: 200,
      selectable: !readOnly,
      editable: !readOnly,
      // activeOn: "up" fixes text editing in Fabric.js v7
      activeOn: "up",
    });

    canvas.add(textbox);
    canvas.setActiveObject(textbox);
    canvas.renderAll();

    if (onChange) {
      onChange(getCanvasData());
    }
  }, [onChange, getCanvasData, readOnly]);

  const addShape = useCallback((type: "rect" | "circle" | "triangle" | "line") => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    let shape: FabricObject;

    switch (type) {
      case "rect":
        shape = new Rect({
          left: 100,
          top: 100,
          width: 100,
          height: 100,
          fill: "#e5e5e5",
          stroke: "#000000",
          strokeWidth: 1,
        });
        break;
      case "circle":
        shape = new Circle({
          left: 100,
          top: 100,
          radius: 50,
          fill: "#e5e5e5",
          stroke: "#000000",
          strokeWidth: 1,
        });
        break;
      case "triangle":
        shape = new Triangle({
          left: 100,
          top: 100,
          width: 100,
          height: 100,
          fill: "#e5e5e5",
          stroke: "#000000",
          strokeWidth: 1,
        });
        break;
      case "line":
        shape = new Line([100, 100, 200, 100], {
          stroke: "#000000",
          strokeWidth: 2,
        });
        break;
    }

    canvas.add(shape);
    canvas.setActiveObject(shape);
    canvas.renderAll();

    if (onChange) {
      onChange(getCanvasData());
    }
  }, [onChange, getCanvasData]);

  const addOutputField = useCallback((slug: string, value: unknown, label?: string) => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    const displayText = label ? `${label}: ${value}` : String(value);
    const textbox = new Textbox(displayText, {
      left: 100,
      top: 100,
      fontSize: 18,
      fill: "#0066B3", // laposte-blue
      fontWeight: "bold",
      backgroundColor: "#CCE0F0", // laposte-blue-light
      padding: 8,
      selectable: !readOnly,
      editable: !readOnly,
      // activeOn: "up" fixes text editing in Fabric.js v7
      activeOn: "up",
    });

    textbox.set("customType", "output_field");
    textbox.set("outputSlug", slug);
    textbox.set("originalValue", value);
    textbox.set("isEditable", true);

    canvas.add(textbox);
    canvas.setActiveObject(textbox);
    canvas.renderAll();

    if (onChange) {
      onChange(getCanvasData());
    }
  }, [onChange, getCanvasData, readOnly]);

  const addImage = useCallback(async (url: string) => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    try {
      const img = await FabricImage.fromURL(url);
      img.set({
        left: 100,
        top: 100,
      });
      
      // Scale down if too large
      const maxSize = 300;
      if (img.width && img.width > maxSize) {
        img.scaleToWidth(maxSize);
      }
      if (img.height && img.height > maxSize) {
        img.scaleToHeight(maxSize);
      }

      canvas.add(img);
      canvas.setActiveObject(img);
      canvas.renderAll();

      if (onChange) {
        onChange(getCanvasData());
      }
    } catch (error) {
      console.error("Failed to add image:", error);
    }
  }, [onChange, getCanvasData]);

  // Add image from data URL (used for clipboard paste)
  const addImageFromDataUrl = useCallback(async (dataUrl: string) => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    try {
      const img = await FabricImage.fromURL(dataUrl);
      
      // Center the image on the canvas
      const imgWidth = img.width || 0;
      const imgHeight = img.height || 0;
      img.set({
        left: (width - imgWidth) / 2,
        top: (height - imgHeight) / 2,
      });
      
      // Scale down if too large (max 80% of canvas size)
      const maxSize = Math.min(width, height) * 0.8;
      if (imgWidth > maxSize || imgHeight > maxSize) {
        const scale = Math.min(maxSize / imgWidth, maxSize / imgHeight);
        img.scale(scale);
      }

      canvas.add(img);
      canvas.setActiveObject(img);
      canvas.renderAll();

      if (onChange) {
        onChange(getCanvasData());
      }
    } catch (error) {
      console.error("Failed to paste image:", error);
    }
  }, [width, height, onChange, getCanvasData]);

  // Add text from clipboard paste
  const addPastedText = useCallback((text: string) => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    // Calculate width based on text length (with min/max constraints)
    const estimatedWidth = Math.min(Math.max(text.length * 8, 100), 600);
    
    const textbox = new Textbox(text, {
      left: (width - estimatedWidth) / 2,
      top: height / 2 - 50,
      fontSize: 16,
      fill: "#000000",
      width: estimatedWidth,
      selectable: !readOnly,
      editable: !readOnly,
      // activeOn: "up" fixes text editing in Fabric.js v7
      activeOn: "up",
    });

    canvas.add(textbox);
    canvas.setActiveObject(textbox);
    canvas.renderAll();

    if (onChange) {
      onChange(getCanvasData());
    }
  }, [width, height, onChange, getCanvasData, readOnly]);

  const addTable = useCallback((rows: number = 3, cols: number = 3) => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    const tableWidth = Math.min(cols * 120, 700);
    const tableHeight = rows * 35;

    // Create default data: header row with "Col N" labels, rest empty
    const data: TableCellValue[][] = Array.from({ length: rows }, (_, r) =>
      Array.from({ length: cols }, (_, c): TableCellValue => (r === 0 ? `Col ${c + 1}` : ""))
    );

    const group = addTableToCanvas(canvas, {
      rows,
      cols,
      data,
      left: 100,
      top: 100,
      width: tableWidth,
      height: tableHeight,
    }, readOnly);

    canvas.setActiveObject(group);
    canvas.renderAll();

    if (onChange) {
      onChange(getCanvasData());
    }
  }, [onChange, getCanvasData, readOnly]);

  const addPlaceholderZone = useCallback((
    placeholderType: "pricing_grid" | "output_value" | "input_value" | "data_table" = "pricing_grid",
    slugValue?: string,
    label?: string
  ) => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    // Determine default label based on type
    const defaultLabel = placeholderType === "pricing_grid" 
      ? "Grille Tarifaire" 
      : placeholderType === "data_table"
        ? "Tableau de donnees"
        : placeholderType === "input_value"
          ? "Valeur Input"
          : "Valeur";
    
    // Use different colors for input zones vs output zones
    const isInputZone = placeholderType === "input_value";
    const isDataTable = placeholderType === "data_table";
    const zoneColor = isDataTable ? "#9B5DE5" : isInputZone ? "#00B2A9" : "#0066B3";
    const zoneFill = isDataTable ? "rgba(155, 93, 229, 0.08)" : isInputZone ? "rgba(0, 178, 169, 0.08)" : "rgba(0, 102, 179, 0.08)";
    
    const zoneWidth = (placeholderType === "pricing_grid" || placeholderType === "data_table") ? 400 : 200;
    const zoneHeight = (placeholderType === "pricing_grid" || placeholderType === "data_table") ? 250 : 50;
    
    const rect = new Rect({
      left: 100,
      top: 100,
      width: zoneWidth,
      height: zoneHeight,
      fill: zoneFill,
      stroke: zoneColor,
      strokeWidth: 2,
      strokeDashArray: [8, 4],
    });

    rect.set("customType", "placeholder_zone");
    rect.set("placeholderType", placeholderType);
    // Set the appropriate slug based on type
    if (placeholderType === "input_value") {
      rect.set("inputSlug", slugValue);
    } else {
      rect.set("outputSlug", slugValue);
    }
    rect.set("placeholderLabel", label || defaultLabel);
    // Set default text color to black
    rect.set("fill", "#000000");

    canvas.add(rect);
    canvas.setActiveObject(rect);
    canvas.renderAll();

    if (onChange) {
      onChange(getCanvasData());
    }
  }, [onChange, getCanvasData]);

  const deleteSelected = useCallback(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    const activeObjects = canvas.getActiveObjects();
    if (activeObjects.length > 0) {
      activeObjects.forEach((obj) => canvas.remove(obj));
      canvas.discardActiveObject();
      canvas.renderAll();

      if (onChange) {
        onChange(getCanvasData());
      }
    }
  }, [onChange, getCanvasData]);

  const setBackgroundColor = useCallback((color: string) => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    canvas.backgroundColor = color;
    canvas.renderAll();

    if (onChange) {
      onChange(getCanvasData());
    }
  }, [onChange, getCanvasData]);

  const toDataURL = useCallback((format: "png" | "jpeg" = "png", quality: number = 1): string => {
    const canvas = fabricRef.current;
    if (!canvas) return "";

    return canvas.toDataURL({
      format,
      quality,
      multiplier: 2, // Higher resolution
    });
  }, []);

  // Get the currently selected element
  const getSelectedElement = useCallback((): FabricObject | null => {
    const canvas = fabricRef.current;
    if (!canvas) return null;
    return canvas.getActiveObject() || null;
  }, []);

  // Update properties of the selected element(s)
  const updateElement = useCallback((properties: Record<string, unknown>) => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    const activeObject = canvas.getActiveObject();
    if (!activeObject) return;

    // Check if this is a table update that requires rebuilding the Group
    const tableProps = ["tableData", "tableRows", "tableCols", "tableHasHeader",
      "tableHasRowHeader", "tableSkipHideZeroRows", "tableHeaderBgColor", "tableBorderColor",
      "tableFontSize", "tableFontFamily", "tableFill", "tableHeaderTextColor"];
    const isTableUpdate = activeObject.customType === "table" &&
      Object.keys(properties).some((key) => tableProps.includes(key));

    if (isTableUpdate && activeObject instanceof Group) {
      // Merge new properties with existing ones
      const currentLeft = activeObject.left || 0;
      const currentTop = activeObject.top || 0;
      const mergedRows = (properties.tableRows as number) ?? activeObject.tableRows ?? 3;
      const mergedCols = (properties.tableCols as number) ?? activeObject.tableCols ?? 3;
      const mergedData = (properties.tableData as TableCellValue[][]) ?? activeObject.tableData ?? [];
      const mergedWidth = (properties.tableWidth as number) ?? activeObject.tableWidth ?? 300;
      const mergedHeight = (properties.tableHeight as number) ?? activeObject.tableHeight ?? 150;
      const mergedHeaderBgColor = (properties.tableHeaderBgColor as string) ?? activeObject.tableHeaderBgColor;
      const mergedBorderColor = (properties.tableBorderColor as string) ?? activeObject.tableBorderColor;
      const mergedHasHeader = (properties.tableHasHeader as boolean) ?? activeObject.tableHasHeader;
      const mergedHasRowHeader = (properties.tableHasRowHeader as boolean) ?? activeObject.tableHasRowHeader;
      const mergedSkipHideZeroRows = (properties.tableSkipHideZeroRows as boolean) ?? activeObject.tableSkipHideZeroRows;
      const mergedFontSize = (properties.tableFontSize as number) ?? activeObject.tableFontSize;
      const mergedFontFamily = (properties.tableFontFamily as string) ?? activeObject.tableFontFamily;
      const mergedFill = (properties.tableFill as string) ?? activeObject.tableFill;
      const mergedHeaderTextColor = (properties.tableHeaderTextColor as string) ?? activeObject.tableHeaderTextColor;

      // Recalculate dimensions if rows/cols changed
      const newWidth = (properties.tableCols !== undefined)
        ? mergedCols * (mergedWidth / (activeObject.tableCols || 3))
        : mergedWidth;
      const newHeight = (properties.tableRows !== undefined)
        ? mergedRows * (mergedHeight / (activeObject.tableRows || 3))
        : mergedHeight;

      // Remove old group
      canvas.remove(activeObject);

      // Rebuild table
      const newGroup = addTableToCanvas(canvas, {
        rows: mergedRows,
        cols: mergedCols,
        data: mergedData,
        left: currentLeft,
        top: currentTop,
        width: newWidth,
        height: newHeight,
        headerBgColor: mergedHeaderBgColor,
        borderColor: mergedBorderColor,
        hasHeader: mergedHasHeader,
        hasRowHeader: mergedHasRowHeader,
        skipHideZeroRows: mergedSkipHideZeroRows,
        fontSize: mergedFontSize,
        fontFamily: mergedFontFamily,
        fill: mergedFill,
        headerTextColor: mergedHeaderTextColor,
      }, readOnly);

      canvas.setActiveObject(newGroup);
      canvas.renderAll();

      if (onChange) {
        onChange(getCanvasData());
      }
      return;
    }

    // If it's a multi-selection, apply properties to all objects in the selection
    if (activeObject instanceof ActiveSelection) {
      activeObject.forEachObject((obj: FabricObject) => {
        obj.set(properties);
        obj.setCoords();
      });
    } else {
      // Single object - apply directly
      activeObject.set(properties);
      activeObject.setCoords();
    }
    
    canvas.renderAll();

    if (onChange) {
      onChange(getCanvasData());
    }
  }, [onChange, getCanvasData, readOnly]);

  // Bring selected element(s) one layer forward
  const bringForward = useCallback(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    const activeObject = canvas.getActiveObject();
    if (!activeObject) return;

    // If it's a multi-selection, bring each object forward
    if (activeObject instanceof ActiveSelection) {
      // Get objects sorted by their current z-index (back to front)
      const objects = activeObject.getObjects().slice().sort((a, b) => {
        return canvas.getObjects().indexOf(b) - canvas.getObjects().indexOf(a);
      });
      objects.forEach((obj: FabricObject) => {
        canvas.bringObjectForward(obj);
      });
    } else {
      canvas.bringObjectForward(activeObject);
    }
    
    canvas.renderAll();

    if (onChange) {
      onChange(getCanvasData());
    }
  }, [onChange, getCanvasData]);

  // Send selected element(s) one layer backward
  const sendBackward = useCallback(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    const activeObject = canvas.getActiveObject();
    if (!activeObject) return;

    // If it's a multi-selection, send each object backward
    if (activeObject instanceof ActiveSelection) {
      // Get objects sorted by their current z-index (front to back)
      const objects = activeObject.getObjects().slice().sort((a, b) => {
        return canvas.getObjects().indexOf(a) - canvas.getObjects().indexOf(b);
      });
      objects.forEach((obj: FabricObject) => {
        canvas.sendObjectBackwards(obj);
      });
    } else {
      canvas.sendObjectBackwards(activeObject);
    }
    
    canvas.renderAll();

    if (onChange) {
      onChange(getCanvasData());
    }
  }, [onChange, getCanvasData]);

  // Bring selected element(s) to the front
  const bringToFront = useCallback(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    const activeObject = canvas.getActiveObject();
    if (!activeObject) return;

    // If it's a multi-selection, bring each object to front
    if (activeObject instanceof ActiveSelection) {
      activeObject.forEachObject((obj: FabricObject) => {
        canvas.bringObjectToFront(obj);
      });
    } else {
      canvas.bringObjectToFront(activeObject);
    }
    
    canvas.renderAll();

    if (onChange) {
      onChange(getCanvasData());
    }
  }, [onChange, getCanvasData]);

  // Send selected element(s) to the back
  const sendToBack = useCallback(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    const activeObject = canvas.getActiveObject();
    if (!activeObject) return;

    // If it's a multi-selection, send each object to back
    if (activeObject instanceof ActiveSelection) {
      activeObject.forEachObject((obj: FabricObject) => {
        canvas.sendObjectToBack(obj);
      });
    } else {
      canvas.sendObjectToBack(activeObject);
    }
    
    canvas.renderAll();

    if (onChange) {
      onChange(getCanvasData());
    }
  }, [onChange, getCanvasData]);

  // Duplicate the selected element(s)
  const duplicateSelected = useCallback(async () => {
    const canvas = fabricRef.current;
    if (!canvas) return;

    const activeObject = canvas.getActiveObject();
    if (!activeObject) return;

    // Clone the object
    const cloned = await activeObject.clone();
    
    // Offset the clone slightly so it's visible
    cloned.set({
      left: (cloned.left || 0) + 20,
      top: (cloned.top || 0) + 20,
    });

    // If it's an ActiveSelection, we need to handle the group
    if (activeObject instanceof ActiveSelection) {
      // For multi-selection, add each cloned object individually
      const clonedSelection = cloned as ActiveSelection;
      clonedSelection.canvas = canvas;
      clonedSelection.forEachObject((obj: FabricObject) => {
        canvas.add(obj);
      });
      clonedSelection.setCoords();
    } else {
      canvas.add(cloned);
    }

    canvas.setActiveObject(cloned);
    canvas.renderAll();

    if (onChange) {
      onChange(getCanvasData());
    }
  }, [onChange, getCanvasData]);

  // Expose methods
  useEffect(() => {
    if (!canvasRef.current) return;

    // Attach methods to canvas element for external access
    (canvasRef.current as any).fabricCanvas = {
      addText,
      addShape,
      addOutputField,
      addImage,
      addTable,
      addPlaceholderZone,
      deleteSelected,
      setBackgroundColor,
      getCanvasData,
      toDataURL,
      getSelectedElement,
      updateElement,
      bringForward,
      sendBackward,
      bringToFront,
      sendToBack,
      duplicateSelected,
    };
  }, [addText, addShape, addOutputField, addImage, addTable, addPlaceholderZone, deleteSelected, setBackgroundColor, getCanvasData, toDataURL, getSelectedElement, updateElement, bringForward, sendBackward, bringToFront, sendToBack, duplicateSelected]);

  // Handle click on container - only focus if not editing text
  const handleContainerClick = useCallback(() => {
    const canvas = fabricRef.current;
    if (!canvas) {
      canvasContainerRef.current?.focus();
      return;
    }
    
    // Check if we're currently editing a textbox
    const activeObject = canvas.getActiveObject();
    const isEditingText = activeObject instanceof Textbox && activeObject.isEditing;
    
    // Only focus container if not editing text (to not steal focus from hidden textarea)
    if (!isEditingText) {
      canvasContainerRef.current?.focus();
    }
  }, []);

  return (
    <div 
      ref={canvasContainerRef}
      className="border rounded-lg overflow-hidden bg-muted inline-block focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
      tabIndex={0}
      title="Cliquez pour activer le collage (Ctrl+V)"
      onClick={handleContainerClick}
    >
      <canvas ref={canvasRef} />
    </div>
  );
}

// Hook to access canvas methods
export function useProposalCanvas(canvasRef: React.RefObject<HTMLCanvasElement>) {
  return {
    addText: (text?: string) => {
      (canvasRef.current as any)?.fabricCanvas?.addText(text);
    },
    addShape: (type: "rect" | "circle" | "triangle" | "line") => {
      (canvasRef.current as any)?.fabricCanvas?.addShape(type);
    },
    addOutputField: (slug: string, value: unknown, label?: string) => {
      (canvasRef.current as any)?.fabricCanvas?.addOutputField(slug, value, label);
    },
    addImage: (url: string) => {
      (canvasRef.current as any)?.fabricCanvas?.addImage(url);
    },
    addTable: (rows?: number, cols?: number) => {
      (canvasRef.current as any)?.fabricCanvas?.addTable(rows, cols);
    },
    addPlaceholderZone: (
      placeholderType?: "pricing_grid" | "output_value" | "input_value" | "data_table",
      slugValue?: string,
      label?: string
    ) => {
      (canvasRef.current as any)?.fabricCanvas?.addPlaceholderZone(placeholderType, slugValue, label);
    },
    deleteSelected: () => {
      (canvasRef.current as any)?.fabricCanvas?.deleteSelected();
    },
    setBackgroundColor: (color: string) => {
      (canvasRef.current as any)?.fabricCanvas?.setBackgroundColor(color);
    },
    getCanvasData: (forExport: boolean = false) => {
      return (canvasRef.current as any)?.fabricCanvas?.getCanvasData(forExport);
    },
    toDataURL: (format?: "png" | "jpeg", quality?: number) => {
      return (canvasRef.current as any)?.fabricCanvas?.toDataURL(format, quality);
    },
    getSelectedElement: () => {
      return (canvasRef.current as any)?.fabricCanvas?.getSelectedElement();
    },
    updateElement: (properties: Record<string, unknown>) => {
      (canvasRef.current as any)?.fabricCanvas?.updateElement(properties);
    },
    bringForward: () => {
      (canvasRef.current as any)?.fabricCanvas?.bringForward();
    },
    sendBackward: () => {
      (canvasRef.current as any)?.fabricCanvas?.sendBackward();
    },
    bringToFront: () => {
      (canvasRef.current as any)?.fabricCanvas?.bringToFront();
    },
    sendToBack: () => {
      (canvasRef.current as any)?.fabricCanvas?.sendToBack();
    },
    duplicateSelected: () => {
      (canvasRef.current as any)?.fabricCanvas?.duplicateSelected();
    },
  };
}

